<template>
  <div class="block">
    <SectionHeading
      :title="$t('Атрибуты')"
      :description="
        !templateView
          ? $t(
              'Выберите необходимые дополнительные блоки с атрибутами для данного нотариального действия'
            )
          : ''
      "
    />

    <template v-if="templateView">
      <ViewGroupList :groups="viewGroups" />
    </template>

    <template v-else>
      <div class="relative w-full">
        <SearchIcon
          class="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none"
        />
        <Input v-model="search" :placeholder="$t('Поиск')" class="pl-9" />
      </div>

      <div class="flex flex-col gap-4">
        <div
          v-for="field in filteredFields"
          :key="field.key"
          class="gray-block"
        >
          <Checkbox
            :model-value="isChecked(field.key)"
            @change="toggleField(field)"
            class="w-full"
          >
            <span class="text-sm text-foreground leading-5 font-medium">
              {{ field.comment || field.name || $t("Не указано") }}
            </span>
          </Checkbox>
        </div>

        <p v-if="!filteredFields.length" class="text-sm text-muted-foreground">
          {{ $t("Ничего не найдено") }}
        </p>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import { Search as SearchIcon } from "lucide-vue-next";
import { Input } from "@/shared/ui/input";
import { getTemplateFieldList } from "@/entities/template";
import ViewGroupList from "@/shared/ui/custom/ViewGroupList.vue";
import Checkbox from "@/shared/ui/checkbox/Checkbox.vue";
import { SectionHeading } from "@/shared/ui/section-heading";

const route = useRoute();

const props = defineProps({
  form: {
    type: Object,
    default: () => ({
      template_fields: {},
    }),
  },
});

const templateFields = ref([]);
const search = ref("");

const templateView = computed(() => {
  return route.name === "constructor_action_item_view";
});

const filteredFields = computed(() => {
  if (!search.value) return templateFields.value;

  const q = search.value.toLowerCase();
  return templateFields.value.filter((item) =>
    (item.comment || item.name).toLowerCase().includes(q)
  );
});

const viewGroups = computed(() => {
  const fields = props.form?.template_fields ?? {};

  const items = Object.entries(fields).map(([key, value]) => ({
    code: key,
    name: value.name,
  }));

  return [
    {
      items,
    },
  ];
});

async function loadFields() {
  const res = await getTemplateFieldList();
  if (res?.success && res.payload?.items?.length) {
    templateFields.value = res.payload.items;
  }
}

function validate() {
  return Promise.resolve(true);
}

function isChecked(key) {
  return Boolean(props.form.template_fields[key]);
}

function toggleField(field) {
  const key = field.key;

  if (isChecked(key)) {
    const copy = { ...props.form.template_fields };
    delete copy[key];
    props.form.template_fields = copy;
    return;
  }

  props.form.template_fields = {
    ...props.form.template_fields,
    [key]: {
      name: field.name,
      name_kz: field.name_kz,
      type: field.type,
      value: field.value ?? "",
      nullable: field.nullable ?? null,
      example: field.example ?? "",
      enums: field.enums ?? [],
    },
  };
}

onMounted(async () => {
  if (
    !props.form.template_fields ||
    typeof props.form.template_fields !== "object"
  ) {
    props.form.template_fields = {};
  }

  await loadFields();
});

defineExpose({ validate });
</script>

<style scoped lang="scss">
.gray-block {
  @apply w-full flex items-center gap-2 rounded-xl px-4 py-2 cursor-pointer transition-colors;
  background-color: hsl(var(--muted)) !important;
  border-radius: var(--radius) !important;

  &:hover {
    background-color: hsl(var(--muted)) !important;
  }
}

.block {
  @apply p-8 h-full overflow-auto rounded-2xl flex flex-col gap-6 w-1/2;
  background-color: hsl(var(--background)) !important;
  border: 1px solid hsl(var(--border)) !important;
  border-radius: 16px !important;
}

@media (max-width: 1500px) {
  .block {
    @apply w-full;
  }
}
</style>
